import { useState, useEffect, type FocusEvent } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import Select, { components, type OptionProps } from "react-select";
import Swal from "sweetalert2";
import Modal from "../../../../../../components/base/Modal";
import type { UnitDto } from "../../../../../../store/admin/unitsStore";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../../../../../../types/components";
import { getCustomSelectStyles } from "../../../../shared";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useSchemaOverrides } from "../../../../../../hooks/useSchemaOverrides";
import { checkVehiclePlateAvailable } from "../../../../../../features/devices/mutations";
import { usePolicyAccess } from "../../../../../../hooks/usePolicyAccess";
import { brand } from "../../../../../../config/theme";
import { MAX_FUEL_CAPACITY_L, isValidFuelCapacity, toFuelCapacity } from "../fuelCapacity";

const CREATE_STATUS_VALUE = "__create_new_status__";

interface AddUnitModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  form: UseFormReturn<UnitDto>;
  types: { value: string; label: string }[];
  fuelTypes: { value: string; label: string }[];
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (data: UnitDto) => void;
}

export default function AddUnitModal({
  themeClasses,
  isDarkMode,
  form,
  types,
  fuelTypes,
  loadingSubmit,
  onClose,
  onSubmit,
}: AddUnitModalProps) {
  const { t } = useTranslation("adminSections");
  const { isVisible, isRequired } = useSchemaOverrides("vehicles", "postVehicle");
  const { canEditPolicy } = usePolicyAccess();
  const [showCreateStatusModal, setShowCreateStatusModal] = useState(false);
  const [newStatusName, setNewStatusName] = useState("");
  const [creatingStatus, setCreatingStatus] = useState(false);
  const [createStatusError, setCreateStatusError] = useState<string | null>(null);
  const [checkingPlate, setCheckingPlate] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = form;

  const inputClassName = `w-full text-sm px-4 py-3 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;

  const { statuses, createStatus, fetchStatuses } = useStatusesStore();
  // Para resellers usar la compañía seleccionada, para carriers usar su propia compañía
  const selectedCompanyId = useCompaniesStore((state) => state.companyId);
  const userCompanyId = localStorage.getItem("userCompanyId");
  const companyId = selectedCompanyId || userCompanyId;
  const baseStatusOptions = getStatusOptionsForEntity(statuses, "units", t, companyId);

  // Cargar statuses cuando el modal se abre
  useEffect(() => {
    if (statuses.length === 0) {
      fetchStatuses({ limit: 100, page: 1 });
    }
  }, [statuses.length, fetchStatuses]);

  // Agregar opción para crear nuevo estado
  const statusOptions = [
    ...baseStatusOptions,
    { value: CREATE_STATUS_VALUE, label: `+ ${t("units.form.createNewStatus", { defaultValue: "Crear nuevo estado" })}` },
  ];

  const handlePlateBlur = async (e: FocusEvent<HTMLInputElement>) => {
    const trimmed = e.target.value.trim();
    if (!trimmed) {
      clearErrors("plate");
      return;
    }

    setCheckingPlate(true);
    try {
      const result = await checkVehiclePlateAvailable(trimmed);
      if (!result.available) {
        setError("plate", { type: "manual", message: result.message! });
      } else {
        clearErrors("plate");
      }
    } finally {
      setCheckingPlate(false);
    }
  };

  const handleStatusChange = (option: any, fieldOnChange: (value: string) => void) => {
    if (option?.value === CREATE_STATUS_VALUE) {
      setShowCreateStatusModal(true);
    } else {
      fieldOnChange(option?.value);
    }
  };

  const handleCreateStatus = async () => {
    if (!newStatusName.trim()) {
      setCreateStatusError(t("units.form.statusNameRequired", { defaultValue: "El nombre del estado es requerido" }));
      return;
    }

    setCreatingStatus(true);
    setCreateStatusError(null);

    try {
      const result = await createStatus({
        name: newStatusName.trim(),
        active: true,
        company_id: companyId,
      });

      if (result.success && result.data) {
        const newStatusId = result.data.id;
        await fetchStatuses({ limit: 100, page: 1 });
        setShowCreateStatusModal(false);
        setNewStatusName("");
        // Usar setTimeout para asegurar que el estado se actualice después del re-render
        setTimeout(() => {
          setValue("status_id", newStatusId);
        }, 100);
        Swal.fire({
          title: t("units.form.success", { defaultValue: "Éxito" }),
          text: t("units.form.statusCreatedSuccess", { defaultValue: "Estado creado correctamente" }),
          icon: "success",
          confirmButtonText: t("units.form.understood", { defaultValue: "Entendido" }),
          confirmButtonColor: brand.primary,
        });
      } else {
        setCreateStatusError(result.error || t("units.form.createStatusError", { defaultValue: "Error al crear el estado" }));
      }
    } catch (err) {
      setCreateStatusError(t("units.form.createStatusError", { defaultValue: "Error al crear el estado" }));
    } finally {
      setCreatingStatus(false);
    }
  };

  // Custom Option component para estilizar la opción de crear
  const CustomOption = (props: OptionProps<any>) => {
    const isCreateOption = props.data.value === CREATE_STATUS_VALUE;
    return (
      <components.Option {...props}>
        <span className={isCreateOption ? "text-primary-500 font-medium" : ""}>
          {props.data.label}
        </span>
      </components.Option>
    );
  };

  return (
    <Modal
      themeClasses={themeClasses}
      onClose={onClose}
      title={t("units.addTitle")}
      content={
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          {isVisible("name") && (
          <div key={"name"} className="flex flex-col gap-2 w-full">
            <label
              htmlFor={"name"}
              className={`text-sm font-medium ${themeClasses.text}`}
            >
              {t("units.fields.name")} {isRequired("name") && <span className="text-red-500">*</span>}
            </label>
            <input
              id={"name"}
              type="text"
              {...register("name" as keyof typeof register, {
                required: isRequired("name") ? t("units.form.requiredField") : false,
                minLength: {
                  value: 3,
                  message: t("units.form.minLength3"),
                },
              })}
              placeholder={t("units.form.namePlaceholder")}
              className={`${inputClassName} ${
                errors["name" as keyof typeof errors] ? "ring-2 ring-red-500" : ""
              }`}
            />
            {errors["name" as keyof typeof errors] && (
              <p className="text-red-500 text-xs">
                {errors["name" as keyof typeof errors]?.message}
              </p>
            )}
          </div>
          )}
          {isVisible("plate") && (() => {
            const plateRegistration = register("plate" as keyof typeof register, {
              required: isRequired("plate") ? t("units.form.requiredField") : false,
              minLength: {
                value: 6,
                message: t("units.form.minLength6"),
              },
              maxLength: {
                value: 8,
                message: t("units.form.maxLength"),
              },
            });

            return (
              <div key={"plate"} className="flex flex-col gap-2 w-full">
                <label
                  htmlFor={"plate"}
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.plate")} {isRequired("plate") && <span className="text-red-500">*</span>}
                </label>
                <input
                  id={"plate"}
                  type="text"
                  {...plateRegistration}
                  onBlur={(e) => {
                    plateRegistration.onBlur(e);
                    handlePlateBlur(e);
                  }}
                  placeholder={t("units.form.platePlaceholder")}
                  className={`${inputClassName} ${
                    errors["plate" as keyof typeof errors] ? "ring-2 ring-red-500" : ""
                  }`}
                />
                {checkingPlate && (
                  <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t("devices.validation.checkingPlate", { defaultValue: "Verificando placa..." })}
                  </p>
                )}
                {errors["plate" as keyof typeof errors] && (
                  <p className="text-red-500 text-xs">
                    {errors["plate" as keyof typeof errors]?.message}
                  </p>
                )}
              </div>
            );
          })()}
          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: t("units.fields.brand"), name: "brand" },
              { label: t("units.fields.model"), name: "model" },
              { label: t("units.fields.year"), name: "year" },
            ]
              .filter((field) => isVisible(field.name))
              .map((field) => (
              <div key={field.name} className="flex flex-col gap-2 w-full">
                <label
                  htmlFor={field.name}
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {field.label} {isRequired(field.name) && <span className="text-red-500">*</span>}
                </label>

                <input
                  id={field.name}
                  type={field.name === "year" ? "number" : "text"}
                  {...register(field.name as keyof typeof register, {
                    required: isRequired(field.name) ? t("units.form.requiredField") : false,
                    ...(field.name === "year"
                      ? {
                          setValueAs: (v: any) =>
                            v === "" || v === null || v === undefined ? undefined : Number(v),
                          min: {
                            value: 1900,
                            message: t("units.form.yearMin"),
                          },
                          max: {
                            value: new Date().getFullYear(),
                            message: t("units.form.yearMax"),
                          },
                        }
                      : {
                          minLength: {
                            value: 3,
                            message: t("units.form.minLength3"),
                          },
                        }
                    ),
                  })}
                  placeholder={t(`units.form.${field.name}Placeholder`)}
                  className={`${inputClassName} ${
                    errors[field.name as keyof typeof errors]
                      ? "ring-2 ring-red-500"
                      : ""
                  }`}
                />

                {errors[field.name as keyof typeof errors] && (
                  <p className="text-red-500 text-xs">
                    {errors[field.name as keyof typeof errors]?.message}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isVisible("type") && (
            <div className="flex flex-col gap-2 w-full">
              <label
                htmlFor="type"
                className={`text-sm font-medium ${themeClasses.text}`}
              >
                {t("units.fields.type")} {isRequired("type") && <span className="text-red-500">*</span>}
              </label>

              <Controller
                name="type"
                control={control}
                rules={{ required: isRequired("type") ? t("units.form.requiredField") : false }}
                render={({ field }) => (
                  <Select
                    {...field}
                    inputId="type"
                    value={types.find((option) => option.value === field.value)}
                    onChange={(option: any) => setValue("type", option.value)}
                    options={types}
                    isSearchable
                    placeholder={t("units.form.selectType")}
                    noOptionsMessage={() => null}
                    styles={getCustomSelectStyles(isDarkMode, !!errors.type)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
              />

              {errors.type && (
                <p className="text-red-500 text-xs">
                  {t(errors.type?.message || "")}
                </p>
              )}
            </div>
            )}

            {isVisible("fuelType") && (
            <div className="flex flex-col gap-2 w-full">
              <label
                htmlFor="fuelType"
                className={`text-sm font-medium ${themeClasses.text}`}
              >
                {t("units.fields.fuelType")} {isRequired("fuelType") && <span className="text-red-500">*</span>}
              </label>

              <Controller
                name="fuelType"
                control={control}
                rules={{ required: isRequired("fuelType") ? t("units.form.requiredField") : false }}
                render={({ field }) => (
                  <Select
                    {...field}
                    inputId="fuelType"
                    value={fuelTypes.find(
                      (option) => option.value === field.value
                    )}
                    onChange={(option: any) =>
                      setValue("fuelType", option.value)
                    }
                    options={fuelTypes}
                    isSearchable
                    placeholder={t("units.form.selectFuelType")}
                    noOptionsMessage={() => null}
                    styles={getCustomSelectStyles(isDarkMode, !!errors.fuelType)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
              />

              {errors.fuelType && (
                <p className="text-red-500 text-xs">
                  {t(errors.fuelType?.message || "")}
                </p>
              )}
            </div>
            )}
          </div>

          {/* Estado */}
          {isVisible("status_id") && (
          <div className="flex flex-col gap-2 w-full sm:col-span-2">
            <label
              htmlFor="status_id"
              className={`text-sm font-medium ${themeClasses.text}`}
            >
              {t("units.fields.status")} {isRequired("status_id") && <span className="text-red-500">*</span>}
            </label>

            <Controller
              name="status_id"
              control={control}
              rules={{ required: isRequired("status_id") ? t("units.form.requiredField") : false }}
              render={({ field }) => (
                <Select
                  {...field}
                  inputId="status_id"
                  value={baseStatusOptions.find((option) => option.value === field.value)}
                  onChange={(option: any) => handleStatusChange(option, (val) => setValue("status_id", val))}
                  options={statusOptions}
                  components={{ Option: CustomOption }}
                  isSearchable
                  placeholder={t("units.form.selectStatus")}
                  noOptionsMessage={() => t("units.form.noStatusesAvailable")}
                  styles={getCustomSelectStyles(isDarkMode, !!errors.status_id)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              )}
            />

            {errors.status_id && (
              <p className="text-red-500 text-xs">
                {t(errors.status_id?.message || "")}
              </p>
            )}
          </div>
          )}

          {/* Campos de Póliza */}
          <div className="sm:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isVisible("policy_id") && (
              <div className="flex flex-col gap-2 w-full">
                <label
                  htmlFor="policy_id"
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.policyId")} {isRequired("policy_id") && <span className="text-red-500">*</span>}
                </label>
                <input
                  id="policy_id"
                  type="text"
                  disabled={!canEditPolicy}
                  {...register("policy_id" as keyof typeof register, {
                    required: isRequired("policy_id") ? t("units.form.requiredField") : false,
                  })}
                  placeholder={t("units.form.policyIdPlaceholder")}
                  className={`${inputClassName} ${
                    errors["policy_id" as keyof typeof errors] ? "ring-2 ring-red-500" : ""
                  } ${!canEditPolicy ? "opacity-60 cursor-not-allowed" : ""}`}
                />
                {errors["policy_id" as keyof typeof errors] && (
                  <p className="text-red-500 text-xs">
                    {errors["policy_id" as keyof typeof errors]?.message}
                  </p>
                )}
              </div>
              )}

              {isVisible("policy_expiry_date") && (
              <div className="flex flex-col gap-2 w-full">
                <label
                  htmlFor="policy_expiry_date"
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.policyExpiryDate")} {isRequired("policy_expiry_date") && <span className="text-red-500">*</span>}
                </label>
                <input
                  id="policy_expiry_date"
                  type="date"
                  disabled={!canEditPolicy}
                  {...register("policy_expiry_date" as keyof typeof register, {
                    required: isRequired("policy_expiry_date") ? t("units.form.requiredField") : false,
                  })}
                  className={`${inputClassName} ${
                    errors["policy_expiry_date" as keyof typeof errors] ? "ring-2 ring-red-500" : ""
                  } ${!canEditPolicy ? "opacity-60 cursor-not-allowed" : ""}`}
                />
                {errors["policy_expiry_date" as keyof typeof errors] && (
                  <p className="text-red-500 text-xs">
                    {errors["policy_expiry_date" as keyof typeof errors]?.message}
                  </p>
                )}
              </div>
              )}
            </div>
            {!canEditPolicy && (
              <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} mt-2`}>
                {t("units.form.policyEditInfo", { defaultValue: "Solo administradores pueden editar la información de póliza" })}
              </p>
            )}
          </div>

          {isVisible("fuelCapacity") && (
          // Toda la fila: si no, empuja "Cancelar" junto al campo y descuadra los botones.
          <div className="flex flex-col gap-2 w-full sm:col-span-2">
            <label htmlFor="fuelCapacity" className={`text-sm font-medium ${themeClasses.text}`}>
              {t("units.fields.fuelCapacity")}
            </label>
            <input
              id="fuelCapacity"
              type="number"
              inputMode="decimal"
              min="0"
              max={MAX_FUEL_CAPACITY_L}
              step="any"
              {...register("fuelCapacity", {
                setValueAs: toFuelCapacity,
                validate: (v) => isValidFuelCapacity(v) || t("units.form.fuelCapacityInvalid"),
              })}
              placeholder={t("units.form.fuelCapacityPlaceholder")}
              className={`${inputClassName} ${errors.fuelCapacity ? "ring-2 ring-red-500" : ""}`}
            />
            {errors.fuelCapacity && <p className="text-red-500 text-xs">{errors.fuelCapacity.message}</p>}
          </div>
          )}

          {/* Botones */}
          <button
            type="button"
            onClick={onClose}
            className={`py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
          >
            {t("units.form.cancel")}
          </button>

          <button
            type="submit"
            disabled={loadingSubmit || checkingPlate}
            className={`bg-primary-500 hover:bg-primary-400 text-gray-900 py-3 px-4 rounded-lg font-medium transition-colors ${
              loadingSubmit || checkingPlate ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loadingSubmit ? t("units.form.submitting") : t("units.form.save")}
          </button>

          {/* Modal para crear estado personalizado */}
          {showCreateStatusModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
              <div className={`${isDarkMode ? "bg-dark-bg" : "bg-white"} rounded-xl max-w-md w-full overflow-hidden border ${themeClasses.cardBorder} shadow-2xl`}>
                <div className={`flex items-center justify-between p-4 border-b ${themeClasses.cardBorder}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                      <i className="ri-add-line text-black text-lg"></i>
                    </div>
                    <h2 className={`font-semibold text-lg ${themeClasses.text}`}>
                      {t("units.form.createCustomStatus", { defaultValue: "Crear Estado Personalizado" })}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateStatusModal(false);
                      setNewStatusName("");
                      setCreateStatusError(null);
                    }}
                    className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
                  >
                    <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
                  </button>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <label className={`block text-sm ${themeClasses.text} mb-2`}>
                      {t("units.form.statusName", { defaultValue: "Nombre del estado" })} *
                    </label>
                    <input
                      type="text"
                      value={newStatusName}
                      onChange={(e) => setNewStatusName(e.target.value)}
                      placeholder={t("units.form.statusNamePlaceholder", { defaultValue: "Ej: En reparación" })}
                      className={inputClassName}
                      disabled={creatingStatus}
                    />
                  </div>

                  <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4`}>
                    {t("units.form.customStatusInfo", { defaultValue: "Este estado personalizado estará disponible solo para tu compañía." })}
                  </p>

                  {createStatusError && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 mb-4">
                      <div className="flex items-start gap-2">
                        <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                        <p className="text-sm text-red-500">{createStatusError}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateStatusModal(false);
                        setNewStatusName("");
                        setCreateStatusError(null);
                      }}
                      disabled={creatingStatus}
                      className={`px-6 py-2.5 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover} transition-colors font-medium`}
                    >
                      {t("units.form.cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateStatus}
                      disabled={!newStatusName.trim() || creatingStatus}
                      className="px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {creatingStatus && <i className="ri-refresh-line animate-spin"></i>}
                      {creatingStatus ? t("units.form.creating", { defaultValue: "Creando..." }) : t("units.form.create", { defaultValue: "Crear" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      }
    />
  );
}

